import { z } from "zod";
import { ROLE_RANK, type Role } from "./schemas/tenant";

// Who sees what. Every menu item (and the sub-sections under some of them) is
// a SECTION. Each role has defaults (today's behavior); the OWNER can change
// any role's set, and override it per person. The effective set rides in the
// person's custom claims as two short lists, relative to the role's defaults:
//   deny  — default sections taken away
//   grant — sections added beyond the role's defaults
// Rules, callables and the app all apply the same formula (canAccess), so a
// URL typed by hand gets the same answer as the menu. The owner is never
// restricted.

export const NAV_GROUPS = ["Work", "Money", "Team", "Setup"] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

export interface AppSection {
  id: string;
  label: string;
  group: NavGroup;
  /** Sub-section of another section (shown under it; needs the parent too). */
  parent?: string;
  /** In-app link (sub-sections point into their parent's page). */
  href: string;
  /** Lowest office role that has it by default. */
  minRole: Exclude<Role, "tech">;
  /** Field techs have it by default (their short field menu). */
  tech?: boolean;
  /** Office pages a field tech can't be given (their data rules are office-only). */
  officeOnly?: boolean;
  /** Never grantable beyond the default (owner-level controls). */
  ownerOnly?: boolean;
}

export const SECTIONS: AppSection[] = [
  { id: "dashboard", label: "Dashboard", group: "Work", href: "/", minRole: "staff", officeOnly: true },
  // Not office-only: the owner can give Field techs the board (read-only — the
  // rules still let them change only their own jobs). Off for techs by default.
  { id: "schedule", label: "Schedule", group: "Work", href: "/schedule", minRole: "staff" },
  { id: "pipeline", label: "Pipeline", group: "Work", href: "/pipeline", minRole: "manager", officeOnly: true },
  { id: "jobs", label: "Jobs", group: "Work", href: "/jobs", minRole: "staff", tech: true },
  { id: "jobs.settings", label: "Job settings", group: "Work", parent: "jobs", href: "/jobs#job-settings", minRole: "admin", officeOnly: true },
  { id: "jobs.export", label: "Export jobs (CSV)", group: "Work", parent: "jobs", href: "/jobs", minRole: "staff", officeOnly: true },
  { id: "requests", label: "Requests", group: "Work", href: "/requests", minRole: "manager", officeOnly: true },
  { id: "requests.chat", label: "AI chat scheduling", group: "Work", parent: "requests", href: "/requests#chat", minRole: "admin", officeOnly: true },
  { id: "projects", label: "Projects", group: "Work", href: "/projects", minRole: "manager", officeOnly: true },
  { id: "workorders", label: "Work orders", group: "Work", href: "/workorders", minRole: "manager", officeOnly: true },
  { id: "intake", label: "Email intake", group: "Work", href: "/intake", minRole: "manager", officeOnly: true },
  { id: "customers", label: "Customers", group: "Work", href: "/customers", minRole: "staff", officeOnly: true },
  { id: "customers.export", label: "Export customers (CSV)", group: "Work", parent: "customers", href: "/customers", minRole: "staff", officeOnly: true },
  { id: "customers.senddocs", label: "Send company documents", group: "Work", parent: "customers", href: "/customers", minRole: "staff", officeOnly: true },
  { id: "customers.portal", label: "Let customers into the portal", group: "Work", parent: "customers", href: "/customers", minRole: "staff", officeOnly: true },
  { id: "referrals", label: "Referrals (Clevis Relay)", group: "Work", href: "/referrals", minRole: "manager", officeOnly: true },
  { id: "estimates", label: "Estimates", group: "Money", href: "/estimates", minRole: "staff", officeOnly: true },
  { id: "invoices", label: "Invoices", group: "Money", href: "/invoices", minRole: "staff", officeOnly: true },
  { id: "expenses", label: "Expenses", group: "Money", href: "/expenses", minRole: "staff", officeOnly: true },
  { id: "pricebook", label: "Pricebook", group: "Money", href: "/pricebook", minRole: "manager", officeOnly: true },
  { id: "inventory", label: "Inventory", group: "Money", href: "/inventory", minRole: "manager", officeOnly: true },
  { id: "reports", label: "Reports", group: "Money", href: "/reports", minRole: "manager", officeOnly: true },
  { id: "purchasing", label: "Purchase orders", group: "Money", href: "/purchasing", minRole: "manager", officeOnly: true },
  { id: "agreements", label: "Maintenance agreements", group: "Money", href: "/agreements", minRole: "manager", officeOnly: true },
  { id: "timeclock", label: "Time Clock", group: "Team", href: "/timeclock", minRole: "staff", tech: true },
  { id: "shifts", label: "Shifts", group: "Team", href: "/shifts", minRole: "staff", tech: true },
  { id: "nearby", label: "Near Me", group: "Team", href: "/nearby", minRole: "staff", tech: true },
  { id: "timesheets", label: "Timesheets", group: "Team", href: "/timesheets", minRole: "manager", officeOnly: true },
  { id: "team", label: "Team", group: "Team", href: "/team", minRole: "staff", officeOnly: true },
  { id: "team.invites", label: "Invite & manage people", group: "Team", parent: "team", href: "/team#invites", minRole: "admin", officeOnly: true },
  { id: "profile", label: "My profile", group: "Team", href: "/profile", minRole: "staff", tech: true },
  { id: "templates", label: "Templates", group: "Setup", href: "/templates", minRole: "manager", officeOnly: true },
  { id: "support", label: "Support", group: "Setup", href: "/support", minRole: "staff", tech: true },
  { id: "texting", label: "Texting", group: "Setup", href: "/texting", minRole: "admin", officeOnly: true },
  { id: "leads", label: "Clevis Ring (lead sources and phone numbers)", group: "Setup", href: "/leads", minRole: "admin", officeOnly: true },
  { id: "mailchimp", label: "Mailchimp", group: "Setup", href: "/mailchimp", minRole: "admin", officeOnly: true },
  { id: "documents", label: "Company documents", group: "Setup", href: "/documents", minRole: "admin", officeOnly: true },
  // Owner only for now: connecting the company's bank to Stripe is the owner's call.
  { id: "payments", label: "Card payments", group: "Setup", href: "/payments", minRole: "admin", officeOnly: true, ownerOnly: true },
  { id: "billing", label: "Billing", group: "Setup", href: "/billing", minRole: "admin", officeOnly: true },
  { id: "migration", label: "Migration", group: "Setup", href: "/migration", minRole: "admin", officeOnly: true },
  { id: "quickbooks", label: "QuickBooks", group: "Setup", href: "/quickbooks", minRole: "admin", officeOnly: true },
  { id: "quickbooks.bills", label: "Bills settings", group: "Setup", parent: "quickbooks", href: "/quickbooks#bills", minRole: "admin", officeOnly: true },
  { id: "quickbooks.estimates", label: "Estimates to QuickBooks", group: "Setup", parent: "quickbooks", href: "/quickbooks#estimates", minRole: "admin", officeOnly: true },
  { id: "access", label: "Roles & access", group: "Setup", href: "/access", minRole: "admin", officeOnly: true, ownerOnly: true },
];
export const SECTION_BY_ID = new Map(SECTIONS.map((s) => [s.id, s]));

/** Today's behavior: what a role has before the owner changes anything. */
export function roleDefault(role: Role, id: string): boolean {
  const s = SECTION_BY_ID.get(id);
  if (!s) return false;
  if (role === "owner") return true;
  if (s.ownerOnly) return false;
  if (role === "tech") return s.tech === true;
  return ROLE_RANK[role] >= ROLE_RANK[s.minRole];
}

/** Whether `role` may ever be given `id` (owner-only and office-only limits). */
export function grantable(role: Role, id: string): boolean {
  const s = SECTION_BY_ID.get(id);
  if (!s || role === "owner") return false;
  if (s.ownerOnly) return false;
  if (role === "tech" && s.officeOnly) return false;
  return true;
}

export const SectionMapSchema = z.record(z.string(), z.boolean());
export const AccessPolicySchema = z.object({
  /** Per role: section → on/off, only where it differs from the default. */
  roles: z.record(z.string(), SectionMapSchema).default({}),
  /** Per person (uid): section → on/off, on top of their role's set. */
  users: z.record(z.string(), SectionMapSchema).default({}),
  updatedAt: z.number().default(0),
});
export type AccessPolicy = z.infer<typeof AccessPolicySchema>;

/** The effective on/off of every section for one person. */
export function effectiveSections(role: Role, policy: Partial<AccessPolicy> | null, uid?: string): Map<string, boolean> {
  const out = new Map<string, boolean>();
  const rolePart = policy?.roles?.[role] ?? {};
  const userPart = (uid && policy?.users?.[uid]) || {};
  for (const s of SECTIONS) {
    let on = roleDefault(role, s.id);
    if (role !== "owner") {
      if (s.id in rolePart && (rolePart[s.id] === false || grantable(role, s.id))) on = rolePart[s.id]!;
      if (s.id in userPart && (userPart[s.id] === false || grantable(role, s.id))) on = userPart[s.id]!;
    }
    out.set(s.id, on);
  }
  // A sub-section needs its parent.
  for (const s of SECTIONS) if (s.parent && !out.get(s.parent)) out.set(s.id, false);
  return out;
}

/** The claim lists: what differs from the role's defaults. */
export function accessClaims(role: Role, policy: Partial<AccessPolicy> | null, uid?: string): { deny: string[]; grant: string[] } {
  const eff = effectiveSections(role, policy, uid);
  const deny: string[] = [];
  const grant: string[] = [];
  for (const s of SECTIONS) {
    const def = roleDefault(role, s.id);
    const on = eff.get(s.id) === true;
    if (def && !on) deny.push(s.id);
    if (!def && on) grant.push(s.id);
  }
  return { deny, grant };
}

/** The one formula, given a role and its claim lists. */
export function canAccess(role: Role | null, id: string, claims: { deny?: unknown; grant?: unknown } | null): boolean {
  if (!role) return false;
  if (role === "owner") return true;
  const deny = Array.isArray(claims?.deny) ? (claims!.deny as string[]) : [];
  const grant = Array.isArray(claims?.grant) ? (claims!.grant as string[]) : [];
  if (grant.includes(id)) return true;
  return roleDefault(role, id) && !deny.includes(id);
}

// Per-person UI preferences (menu order, favorites, dashboard widgets).
export const UiPrefsSchema = z.object({
  /** Group order, and item order inside each group (section ids). */
  navGroups: z.array(z.string()).max(14).optional(),
  navItems: z.record(z.string(), z.array(z.string()).max(60)).optional(),
  /** Pinned at the top of the menu: any section or sub-section. */
  favorites: z.array(z.string()).max(30).optional(),
  dashboard: z
    .object({ order: z.array(z.string()).max(30), hidden: z.array(z.string()).max(30) })
    .optional(),
  /** The department (or group) Jobs, Schedule, Timesheets and the dashboard open on:
   * an id, "all", or "none" (no department). Unset = the person's own department. */
  department: z.string().max(64).optional(),
  /** The floating to-do button: shown (default) or hidden by the person. */
  todoDock: z.enum(["shown", "hidden"]).optional(),
  /** How a Clevis Ring call shows on this person's screen: a bar across the top (default), a small note in the corner, or not at all. */
  callerPop: z.enum(["banner", "corner", "off"]).optional(),
  updatedAt: z.number().optional(),
});
export type UiPrefs = z.infer<typeof UiPrefsSchema>;

// The company's menu: its groups, their names and order, and which items go
// in each. The owner sets it (Roles & access → Company menu); each person can
// still reorder within it and pin Favorites. No layout = the standard groups.
export const MenuGroupSchema = z.object({
  id: z.string().min(1).max(40),
  label: z.string().trim().min(1).max(40),
  /** Section ids, in order. */
  items: z.array(z.string().max(40)).max(40),
});
export const MenuLayoutSchema = z.object({
  groups: z.array(MenuGroupSchema).max(12).default([]),
  updatedAt: z.number().default(0),
});
export type MenuLayout = z.infer<typeof MenuLayoutSchema>;
export type MenuGroup = z.infer<typeof MenuGroupSchema>;

/** Top-level menu items (sections without a parent). */
export const MENU_ITEM_IDS = SECTIONS.filter((s) => !s.parent).map((s) => s.id);

export const MENU_PRESETS: Record<"standard" | "byWork", { label: string; groups: MenuGroup[] }> = {
  standard: {
    label: "Clevis standard (4 groups)",
    groups: NAV_GROUPS.map((g) => ({ id: g, label: g, items: SECTIONS.filter((s) => !s.parent && s.group === g).map((s) => s.id) })),
  },
  byWork: {
    label: "By how people work (8 groups)",
    groups: [
      { id: "home", label: "Home", items: ["dashboard"] },
      { id: "sales", label: "Sales and customers", items: ["requests", "customers", "referrals", "pipeline", "estimates"] },
      { id: "ops", label: "Jobs and operations", items: ["schedule", "jobs", "projects", "workorders", "intake", "nearby"] },
      { id: "purchasing", label: "Purchasing and inventory", items: ["purchasing", "inventory", "pricebook"] },
      { id: "finance", label: "Finance and reporting", items: ["invoices", "agreements", "expenses", "reports", "quickbooks", "payments"] },
      { id: "people", label: "People and time", items: ["team", "shifts", "timeclock", "timesheets", "profile"] },
      { id: "comms", label: "Communications and documents", items: ["texting", "leads", "mailchimp", "documents"] },
      { id: "admin", label: "Administration and support", items: ["templates", "migration", "billing", "access", "support"] },
    ],
  },
};
